import { useCallback, useEffect, useMemo, useState } from 'react';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { toast } from 'sonner';
import { Eraser, Loader2, ScrollText, ShieldCheck, UserPlus, UserX } from 'lucide-react';

import { useAuth } from '@chainlit/react-client';

import { apiClient, type AdminUserSummary } from '@/api';
import PendingSignupsSection from '@/components/admin/PendingSignupsSection';
import CreateUserDialog from '@/components/admin/CreateUserDialog';
import UserCard from '@/components/admin/UserCard';
import UsersOverview from '@/components/admin/UsersOverview';
import UserTable from '@/components/admin/UserTable';
import type { RowAction, UserRowHandlers } from '@/components/admin/userRow';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { useIsMobile } from '@/hooks/use-mobile';
import { visibleUsers, type SortKey, type UserFilter } from '@/lib/userStats';
import {
  adminUserDetailDialogOpenState,
  adminUserDetailIdentifierState,
  adminUsersState,
  auditLogDialogOpenState,
  auditLogUserIdentifierState,
  signupsEnabledState
} from '@/state/project';

type BusyMap = Record<string, RowAction | null>;

const USERS_PER_PAGE = 10;

const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: 'newest', label: 'Newest first' },
  { value: 'oldest', label: 'Oldest first' },
  { value: 'recent-login', label: 'Recently active' },
  { value: 'identifier', label: 'Name (A–Z)' },
  { value: 'tokens', label: 'Most tokens' }
];

const FILTER_OPTIONS: { value: UserFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'admins', label: 'Admins' },
  { value: 'disabled', label: 'Disabled' },
  { value: 'temporary', label: 'Temp' },
  { value: 'never-logged-in', label: 'Never logged in' }
];

export default function AdminSection() {
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const [users, setUsers] = useRecoilState(adminUsersState);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<BusyMap>({});
  const [confirmDelete, setConfirmDelete] = useState<AdminUserSummary | null>(null);
  const [confirmPurge, setConfirmPurge] = useState<AdminUserSummary | null>(null);
  const [purgeText, setPurgeText] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<UserFilter>('all');
  const [sortKey, setSortKey] = useState<SortKey>('newest');
  const [page, setPage] = useState(1);
  const setDetailIdentifier = useSetRecoilState(adminUserDetailIdentifierState);
  const setDetailOpen = useSetRecoilState(adminUserDetailDialogOpenState);
  const setAuditIdentifier = useSetRecoilState(auditLogUserIdentifierState);
  const setAuditOpen = useSetRecoilState(auditLogDialogOpenState);
  const [signupsEnabled, setSignupsEnabled] = useRecoilState(signupsEnabledState);

  const openDetail = useCallback(
    (target: AdminUserSummary) => {
      setDetailIdentifier(target.identifier);
      setDetailOpen(true);
    },
    [setDetailIdentifier, setDetailOpen]
  );

  const openAuditLog = useCallback(
    (identifier: string | null) => {
      setAuditIdentifier(identifier);
      setAuditOpen(true);
    },
    [setAuditIdentifier, setAuditOpen]
  );

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiClient.listAdminUsers();
      setUsers(response.users);
    } catch (error) {
      console.error('Failed to load admin users', error);
      toast.error('Could not load user list');
    } finally {
      setLoading(false);
    }
  }, [setUsers]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    apiClient
      .getSignupPolicy()
      .then((r) => setSignupsEnabled(r.signups_enabled))
      .catch(() => {});
  }, [setSignupsEnabled]);

  const handleToggleSignups = async () => {
    const next = !signupsEnabled;
    try {
      const r = await apiClient.setSignupPolicy(next);
      setSignupsEnabled(r.signups_enabled);
      toast.success(`New sign-ups ${r.signups_enabled ? 'enabled' : 'disabled'}`);
    } catch (error: any) {
      toast.error(error?.message || 'Signup policy update failed');
    }
  };

  const setRowBusy = (identifier: string, action: RowAction | null) =>
    setBusy((prev) => ({ ...prev, [identifier]: action }));

  const applyUpdate = useCallback(
    (updated: AdminUserSummary) => {
      setUsers((prev) =>
        prev.map((row) => (row.identifier === updated.identifier ? updated : row))
      );
    },
    [setUsers]
  );

  const handleToggleAdmin = async (target: AdminUserSummary) => {
    const nextRole = target.role === 'admin' ? 'user' : 'admin';
    setRowBusy(target.identifier, 'role');
    try {
      const updated = await apiClient.setAdminUserRole(target.identifier, nextRole);
      applyUpdate(updated);
      toast.success(`${target.identifier} → ${nextRole}`);
    } catch (error: any) {
      toast.error(error?.message || 'Role update failed');
    } finally {
      setRowBusy(target.identifier, null);
    }
  };

  const handleToggleDisabled = async (target: AdminUserSummary) => {
    setRowBusy(target.identifier, 'disabled');
    try {
      const updated = await apiClient.setAdminUserDisabled(
        target.identifier,
        !target.disabled
      );
      applyUpdate(updated);
      toast.success(
        `${target.identifier} ${updated.disabled ? 'disabled' : 'enabled'}`
      );
    } catch (error: any) {
      toast.error(error?.message || 'Disable update failed');
    } finally {
      setRowBusy(target.identifier, null);
    }
  };

  const handlePasswordReset = async (target: AdminUserSummary) => {
    setRowBusy(target.identifier, 'reset');
    try {
      const result = await apiClient.triggerAdminPasswordReset(target.identifier);
      toast.success(`Password reset email sent to ${result.email}`);
    } catch (error: any) {
      toast.error(error?.message || 'Password reset failed');
    } finally {
      setRowBusy(target.identifier, null);
    }
  };

  const handleLegalReset = async (target: AdminUserSummary) => {
    setRowBusy(target.identifier, 'legal-reset');
    try {
      const result = await apiClient.resetUserLegal(target.identifier);
      toast.success(
        result.deleted
          ? `Reset legal disclaimer for ${target.identifier}`
          : `${target.identifier} had no acceptance row to reset`
      );
    } catch (error: any) {
      toast.error(error?.message || 'Legal reset failed');
    } finally {
      setRowBusy(target.identifier, null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return;
    const target = confirmDelete;
    setRowBusy(target.identifier, 'delete');
    try {
      await apiClient.deleteAdminUser(target.identifier);
      setUsers((prev) => prev.filter((row) => row.identifier !== target.identifier));
      toast.success(`Deleted ${target.identifier}`);
      setConfirmDelete(null);
    } catch (error: any) {
      toast.error(error?.message || 'Delete failed');
    } finally {
      setRowBusy(target.identifier, null);
    }
  };

  const handleConfirmPurge = async () => {
    if (!confirmPurge || purgeText !== confirmPurge.identifier) return;
    const target = confirmPurge;
    setRowBusy(target.identifier, 'purge');
    try {
      const result = await apiClient.purgeAdminUserData(target.identifier);
      setUsers((prev) => prev.filter((row) => row.identifier !== target.identifier));
      toast.success(`Purged ${target.identifier} (${result.threads_deleted} threads deleted)`);
      setConfirmPurge(null);
      setPurgeText('');
    } catch (error: any) {
      toast.error(error?.message || 'Purge failed');
    } finally {
      setRowBusy(target.identifier, null);
    }
  };

  const visible = useMemo(
    () => visibleUsers(users, { query, filter, sortKey }),
    [users, query, filter, sortKey]
  );

  const pageCount = Math.max(1, Math.ceil(visible.length / USERS_PER_PAGE));
  const currentPage = Math.min(page, pageCount);
  const pageRows = visible.slice(
    (currentPage - 1) * USERS_PER_PAGE,
    currentPage * USERS_PER_PAGE
  );

  useEffect(() => {
    setPage(1);
  }, [query, filter, sortKey]);

  const handlersFor = (row: AdminUserSummary): UserRowHandlers => ({
    isSelf: user?.identifier === row.identifier,
    busy: busy[row.identifier] || null,
    onOpenDetail: () => openDetail(row),
    onToggleAdmin: () => handleToggleAdmin(row),
    onToggleDisabled: () => handleToggleDisabled(row),
    onPasswordReset: () => handlePasswordReset(row),
    onLegalReset: () => handleLegalReset(row),
    onAuditLog: () => openAuditLog(row.identifier),
    onPurge: () => {
      setPurgeText('');
      setConfirmPurge(row);
    },
    onDelete: () => setConfirmDelete(row)
  });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <div className="min-w-0">
          <h3 className="text-base font-semibold flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-primary shrink-0" />
            Admin
          </h3>
          <p className="text-sm text-muted-foreground">
            Manage user roles and access. Demoting, disabling, or deleting a
            user takes effect at their next login.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" onClick={() => setCreateOpen(true)} className="gap-1">
            <UserPlus className="h-3 w-3" />
            Create user
          </Button>
          <Button size="sm" variant="outline" onClick={refresh} disabled={loading}>
            {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Refresh'}
          </Button>
          <Button size="sm" variant="outline" onClick={() => openAuditLog(null)} className="gap-1">
            <ScrollText className="h-3 w-3" />
            Audit Log
          </Button>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <Switch checked={signupsEnabled} onCheckedChange={handleToggleSignups} />
          <span>Allow new sign-ups</span>
        </label>
      </div>

      <CreateUserDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(detail) => {
          setUsers((prev) => {
            const summary = {
              id: detail.id,
              identifier: detail.identifier,
              role: detail.role,
              disabled: detail.disabled,
              createdAt: detail.createdAt,
              last_login_at: detail.last_login_at,
              provider: detail.provider,
              total_tokens: detail.total_tokens,
              is_temporary: detail.is_temporary,
              temp_expires_at: detail.temp_expires_at
            };
            const rest = prev.filter((u) => u.identifier !== detail.identifier);
            return [summary, ...rest];
          });
        }}
      />

      <PendingSignupsSection />

      <UsersOverview users={users} />

      {users.length > 0 ? (
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search users…"
              autoComplete="off"
              className="h-8 min-w-[10rem] flex-1"
            />
            <Select value={sortKey} onValueChange={(value) => setSortKey(value as SortKey)}>
              <SelectTrigger className="h-8 w-[9.5rem]" aria-label="Sort users">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SORT_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-wrap gap-1">
            {FILTER_OPTIONS.map((option) => (
              <Button
                key={option.value}
                size="sm"
                variant={filter === option.value ? 'default' : 'outline'}
                className="h-6 px-2 text-[10px]"
                onClick={() => setFilter(option.value)}
              >
                {option.label}
              </Button>
            ))}
          </div>
          <p className="text-[10px] text-muted-foreground">
            Showing {pageRows.length.toLocaleString()} of{' '}
            {visible.length.toLocaleString()}
            {visible.length === users.length
              ? ''
              : ` (filtered from ${users.length.toLocaleString()})`}
          </p>
        </div>
      ) : null}

      {users.length === 0 && !loading ? (
        <p className="text-sm text-muted-foreground">No users found.</p>
      ) : null}

      {users.length > 0 && visible.length === 0 ? (
        <p className="text-sm text-muted-foreground">No users match this search.</p>
      ) : null}

      {visible.length > 0 ? (
        isMobile ? (
          <div className="flex flex-col gap-2">
            {pageRows.map((row) => (
              <UserCard key={row.id} row={row} handlers={handlersFor(row)} />
            ))}
          </div>
        ) : (
          <UserTable
            rows={pageRows}
            sortKey={sortKey}
            onSort={setSortKey}
            handlersFor={handlersFor}
          />
        )
      ) : null}

      {pageCount > 1 ? (
        <div className="flex items-center justify-between gap-2">
          <Button
            size="sm"
            variant="outline"
            className="h-7 px-2 text-[10px]"
            onClick={() => setPage(currentPage - 1)}
            disabled={currentPage <= 1}
          >
            Previous
          </Button>
          <span className="text-[10px] text-muted-foreground">
            Page {currentPage} of {pageCount}
          </span>
          <Button
            size="sm"
            variant="outline"
            className="h-7 px-2 text-[10px]"
            onClick={() => setPage(currentPage + 1)}
            disabled={currentPage >= pageCount}
          >
            Next
          </Button>
        </div>
      ) : null}

      <AlertDialog
        open={confirmDelete !== null}
        onOpenChange={(next) => !next && setConfirmDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              <span className="inline-flex items-center gap-2">
                <UserX className="h-5 w-5 text-destructive" />
                Delete {confirmDelete?.identifier}?
              </span>
            </AlertDialogTitle>
            <AlertDialogDescription>
              Removes the Chainlit user row, the credential row, and the OAuth
              whitelist entry. Threads and chat history orphan but remain in
              the database (`userId` set to NULL). Cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog
        open={confirmPurge !== null}
        onOpenChange={(next) => {
          if (!next) {
            setConfirmPurge(null);
            setPurgeText('');
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              <span className="inline-flex items-center gap-2">
                <Eraser className="h-5 w-5 text-destructive" />
                Delete all data for {confirmPurge?.identifier}?
              </span>
            </AlertDialogTitle>
            <AlertDialogDescription>
              Permanently deletes this user and <strong>every thread they own</strong> —
              messages, steps, elements, and feedback included. Only this user&apos;s data is
              affected. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              Type{' '}
              <span className="font-mono font-semibold text-foreground">{confirmPurge?.identifier}</span> to
              confirm.
            </p>
            <Input
              value={purgeText}
              onChange={(e) => setPurgeText(e.target.value)}
              placeholder={confirmPurge?.identifier}
              autoComplete="off"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmPurge}
              disabled={purgeText !== confirmPurge?.identifier}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete all data
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
